import { oc } from "@orpc/contract";
import { openapi } from "@orpc/openapi";
import { AssetId, AssetUsageRequest } from "@pax/api-contract/assets/v1alpha1";
import { SurfaceBan } from "@pax/api-contract/moderation/v1alpha1";
import { z } from "zod";
import { type ContractService, errors, paxRoute } from "../../http";
import { AssetSearchQuery, AssetSearchResponse } from "./assets";
import { LobbyProposalResult, LobbyProposalSchema } from "./lobby";
import {
  DeltaRecordQuery,
  DeltaRecordResponse,
  EditSkillRequest,
  ForkGameRequest,
  ForkGameResponse,
  GameId,
  GameIdParams,
  GameSessionSummary,
  LobbyCommandResponse,
  NotifyPayersRequest,
  PlayerConnection,
  ProgressRequest,
  ProposalResult,
  ProposalSchema,
  RefreshGameResponse,
  ReportRunnerEventRequest,
  ReportRunnerEventResponse,
  ScorecardSubmission,
  SelectGameMasterRequest,
  StartedAgent,
  StartGraderRequest,
  StartPlayerAgentRequest,
  TypingRequest,
} from "./schemas";

export const service = {
  group: "games",
  version: "v1alpha1",
  stage: "alpha",
  title: "Pax Game Server API",
  description: "The authoritative record of committed deltas and live protocol for each game.",
} as const satisfies ContractService;

const serviceAuth = {
  kind: "service",
  detail: "Requires an API service access token with the games:manage scope.",
  caller: "api",
  scope: "games:manage",
} as const;
const runnerAuth = {
  kind: "service",
  detail: "Requires an agent-runner service access token with the game-masters:report scope.",
  caller: "agent-runner",
  scope: "game-masters:report",
} as const;
/** Every route refuses a missing or invalid credential, and one that doesn't grant the route. */
const refusals = { ...errors.unauthorized, ...errors.forbidden } as const;

const serviceOrGameTokenAuth = {
  kind: "serviceOrGameToken",
  detail:
    "Requires an API service access token with the games:manage scope or a live bearer game token.",
  scope: "games:manage",
} as const;

const anyGameTokenAuth = {
  kind: "gameToken",
  detail: "Requires a live bearer game token for a player or the game master.",
} as const;
const gameMasterGameTokenAuth = {
  kind: "gameToken",
  detail: "Requires a live bearer game-master game token.",
  roles: ["gameMaster"],
} as const;
const agentGameTokenAuth = {
  kind: "gameToken",
  detail:
    "Requires a live bearer game token bound to an agent: the game master's, a grader's, or a player agent's.",
  agentsOnly: true,
} as const;

export const propose = oc
  .meta(
    paxRoute({ auth: anyGameTokenAuth, policy: { session: true } }),
    openapi({
      method: "POST",
      path: "/games/v1alpha1/{id}/proposals",
      operationId: "games.v1alpha1.propose",
      summary: "Propose deltas.",
      description:
        "Validates the deltas against the token's role and the world, then commits them together or not at all. Committed deltas reach every connected socket. Sending a committed idempotency key again returns the revision it committed through; a rejected proposal is checked again.",
      inputStructure: "detailed",
      successDescription: "Whether the proposal committed, and the revision to wait for.",
    }),
  )
  .errors({ ...errors.badRequest, ...refusals })
  .input(z.object({ params: GameIdParams, body: ProposalSchema }))
  .output(ProposalResult);

const playerGameTokenAuth = {
  kind: "gameToken",
  detail: "Requires a live bearer player game token.",
  roles: ["player"],
} as const;

export const proposeLobby = oc
  .meta(
    paxRoute({ auth: playerGameTokenAuth, policy: { session: true } }),
    openapi({
      method: "POST",
      path: "/games/v1alpha1/{id}/lobby",
      operationId: "games.v1alpha1.proposeLobby",
      summary: "Propose changes to the lobby.",
      description:
        "Checks the operations against the member's roles, moderates what they wrote, then commits them together or not at all. Committed lobby deltas reach every member's socket. Sending a committed idempotency key again returns the revision it committed through.",
      inputStructure: "detailed",
      successDescription: "Whether the proposal committed, and the lobby revision to wait for.",
    }),
  )
  .errors({ ...errors.badRequest, ...refusals })
  .input(z.object({ params: GameIdParams, body: LobbyProposalSchema }))
  .output(LobbyProposalResult);

export const reportProgress = oc
  .meta(
    paxRoute({ auth: gameMasterGameTokenAuth, policy: { session: true } }),
    openapi({
      method: "POST",
      path: "/games/v1alpha1/{id}/progress",
      operationId: "games.v1alpha1.reportProgress",
      summary: "Tell players what the game master is doing.",
      description: "Broadcasts a `progress` frame to connected players. Nothing is committed.",
      inputStructure: "detailed",
      successStatus: 204,
      successDescription: "The progress was broadcast.",
    }),
  )
  .errors({ ...errors.badRequest, ...refusals })
  .input(z.object({ params: GameIdParams, body: ProgressRequest }));

export const reportTyping = oc
  .meta(
    paxRoute({ auth: gameMasterGameTokenAuth, policy: { session: true } }),
    openapi({
      method: "POST",
      path: "/games/v1alpha1/{id}/typing",
      operationId: "games.v1alpha1.reportTyping",
      summary: "Show players that a thread member is typing.",
      description:
        "Broadcasts a `typing` frame to the players who can read the thread. Nothing is committed, and typing nobody refreshes expires.",
      inputStructure: "detailed",
      successStatus: 204,
      successDescription: "The typing was broadcast.",
    }),
  )
  .errors({ ...errors.badRequest, ...refusals })
  .input(z.object({ params: GameIdParams, body: TypingRequest }));

const lobbyErrors = {
  ...errors.badRequest,
  ...refusals,
  CONFLICT: { message: "The world rejected the command." },
} as const;

export const connect = oc
  .meta(
    paxRoute({
      auth: {
        kind: "session",
        detail:
          "Requires the browser's sign-in cookie. The game server has the Product API confirm the account is a member and not banned.",
      },
      policy: { session: true },
    }),
    openapi({
      method: "POST",
      path: "/games/v1alpha1/{id}/connect",
      operationId: "games.v1alpha1.connect",
      summary: "Connect to a game.",
      description:
        "Wakes the game if it was idle, and mints a short-lived player game token for the socket.",
      inputStructure: "detailed",
      successStatus: 201,
      successDescription: "A live player game token.",
    }),
  )
  .errors({
    ...errors.badRequest,
    UNAUTHORIZED: { message: "Sign in first." },
    FORBIDDEN: {
      message: "This account is banned; data holds its ban from live games, when it has one.",
      data: z.strictObject({ ban: SurfaceBan.nullable() }),
    },
    NOT_FOUND: { message: "The game does not exist or the current user is not a member." },
    ...errors.worldUnavailable,
  })
  .input(z.object({ params: GameIdParams }))
  .output(PlayerConnection);

export const editSkill = oc
  .meta(
    paxRoute({ auth: serviceAuth, policy: { session: true } }),
    openapi({
      method: "POST",
      path: "/games/v1alpha1/{id}/edit-skill",
      operationId: "games.v1alpha1.editSkill",
      summary: "Create, update, or delete a skill.",
      description:
        "Commits one `skill.*` delta to the game master as the system actor, with the member as its `responsibleUser`.",
      inputStructure: "detailed",
      successDescription: "The revision the edit committed through.",
    }),
  )
  .errors({
    ...lobbyErrors,
    PRECONDITION_FAILED: {
      message: "The skill changed after the revision the update was based on.",
    },
  })
  .input(z.object({ params: GameIdParams, body: EditSkillRequest }))
  .output(LobbyCommandResponse);

export const selectGameMaster = oc
  .meta(
    paxRoute({ auth: serviceAuth, policy: { session: true } }),
    openapi({
      method: "PUT",
      path: "/games/v1alpha1/{id}/game-master",
      operationId: "games.v1alpha1.selectGameMaster",
      summary: "Choose the game master.",
      description:
        "Retires the current game master, if there is one, gives the role to a new agent of this version, and starts it when players are connected or a jump is active. The system commits `gameMaster.assigned`.",
      inputStructure: "detailed",
      successDescription: "The session's head revision.",
    }),
  )
  .errors(lobbyErrors)
  .input(z.object({ params: GameIdParams, body: SelectGameMasterRequest }))
  .output(LobbyCommandResponse);

export const remove = oc
  .meta(
    paxRoute({ auth: serviceAuth, policy: { session: true } }),
    openapi({
      method: "DELETE",
      path: "/games/v1alpha1/{id}",
      operationId: "games.v1alpha1.remove",
      summary: "Delete a game session.",
      description: "Stops its agents and drops all durable session storage.",
      inputStructure: "detailed",
      successStatus: 204,
      successDescription: "The session was deleted.",
    }),
  )
  .errors({
    ...errors.badRequest,
    ...refusals,
  })
  .input(z.object({ params: GameIdParams }));

export const notifyPayers = oc
  .meta(
    paxRoute({ auth: serviceAuth, policy: { session: true } }),
    openapi({
      method: "POST",
      path: "/games/v1alpha1/{id}/payer-notices",
      operationId: "games.v1alpha1.notifyPayers",
      summary: "Tell paying players something changed about their credits.",
      description:
        "Sends the signal to each named player's open sockets. Players with no open socket miss it.",
      inputStructure: "detailed",
      successStatus: 204,
      successDescription: "The signal was sent to every open socket it reaches.",
    }),
  )
  .errors({
    ...errors.badRequest,
    ...refusals,
  })
  .input(z.object({ params: GameIdParams, body: NotifyPayersRequest }));

export const refresh = oc
  .meta(
    paxRoute({ auth: serviceAuth, policy: { session: true } }),
    openapi({
      method: "POST",
      path: "/games/v1alpha1/{id}/refresh",
      operationId: "games.v1alpha1.refresh",
      summary: "Tell a game something the API holds about it changed.",
      description:
        "The session reads the game from the API again, in its commit queue, and answers with the record's latest revision then. Anyone the API no longer lists as a member is taken off their perspective and out of every connection at once, and every connection hears `game_changed`. The API sends it while a connection is open, and whenever a review needs the game to hold still.",
      inputStructure: "detailed",
      successDescription: "The session holds what the API holds.",
    }),
  )
  .errors({
    ...errors.badRequest,
    ...refusals,
  })
  .input(z.object({ params: GameIdParams }))
  .output(RefreshGameResponse);

export const get = oc
  .meta(
    paxRoute({ auth: serviceAuth, policy: { session: true } }),
    openapi({
      method: "GET",
      path: "/games/v1alpha1/{id}",
      operationId: "games.v1alpha1.get",
      summary: "Get a game-session summary.",
      description: "Reads the head revision and the game master's status.",
      inputStructure: "detailed",
      successDescription: "Current session summary.",
    }),
  )
  .errors({
    ...refusals,
    NOT_FOUND: { message: "This game server has no state for that game." },
  })
  .input(z.object({ params: GameIdParams }))
  .output(GameSessionSummary);

export const reportRunnerEvent = oc
  .meta(
    paxRoute({ auth: runnerAuth, policy: { session: true } }),
    openapi({
      method: "POST",
      path: "/games/v1alpha1/{id}/runner-events",
      operationId: "games.v1alpha1.reportRunnerEvent",
      summary: "Report that an agent started or stopped.",
      description:
        "Updates the session's view of the agent's container. A stop the session did not ask for, or one that did not exit cleanly, replaces the agent.",
      inputStructure: "detailed",
      successDescription: "Whether the event named an agent the session knows.",
    }),
  )
  .errors({
    ...errors.badRequest,
    ...refusals,
  })
  .input(z.object({ params: GameIdParams, body: ReportRunnerEventRequest }))
  .output(ReportRunnerEventResponse);

export const readDeltas = oc
  .meta(
    paxRoute({ auth: serviceOrGameTokenAuth, policy: { session: true } }),
    openapi({
      method: "GET",
      path: "/games/v1alpha1/{id}/deltas",
      operationId: "games.v1alpha1.readDeltas",
      summary: "Read committed deltas.",
      description: "Returns accepted deltas after a revision; rejected proposals never appear.",
      inputStructure: "detailed",
      successDescription: "Committed deltas.",
    }),
  )
  .errors({ ...errors.badRequest, ...refusals })
  .input(z.object({ params: GameIdParams, query: DeltaRecordQuery }))
  .output(DeltaRecordResponse);

export const fork = oc
  .meta(
    paxRoute({ auth: serviceAuth, policy: { session: true } }),
    openapi({
      method: "POST",
      path: "/games/v1alpha1/{id}/fork",
      operationId: "games.v1alpha1.fork",
      summary: "Fork a game at a revision.",
      description:
        "Writes the record through `throughRevision`, then takes every user but the one forking off their perspective, as the new game's World artifact. The new game hydrates from it like any other; record it with the Product API to open it.",
      inputStructure: "detailed",
      successStatus: 201,
      successDescription: "The new game's artifact.",
    }),
  )
  .errors(lobbyErrors)
  .input(z.object({ params: GameIdParams, body: ForkGameRequest }))
  .output(ForkGameResponse);

const graderGameTokenAuth = {
  kind: "gameToken",
  detail: "Requires a live bearer grader game token.",
  roles: ["grader"],
} as const;

export const startGrader = oc
  .meta(
    paxRoute({ auth: serviceAuth, policy: { session: true } }),
    openapi({
      method: "POST",
      path: "/games/v1alpha1/{id}/graders",
      operationId: "games.v1alpha1.startGrader",
      summary: "Start a grader on a finished game.",
      description:
        "Starts the grader's agent with a grader game token for this game, and hands it the assignment as it starts. The grader reads the complete record and submits one scorecard, which the game passes to the Product API. Starting the same agent id again changes nothing.",
      inputStructure: "detailed",
      successStatus: 201,
      successDescription: "The grader started.",
    }),
  )
  .errors({ ...errors.badRequest, ...refusals, ...errors.worldUnavailable })
  .input(z.object({ params: GameIdParams, body: StartGraderRequest }))
  .output(StartedAgent);

export const submitScorecard = oc
  .meta(
    paxRoute({ auth: graderGameTokenAuth, policy: { session: true } }),
    openapi({
      method: "POST",
      path: "/games/v1alpha1/{id}/scorecard",
      operationId: "games.v1alpha1.submitScorecard",
      summary: "Submit the grader's scorecard.",
      description:
        "Passes the scorecard to the Product API, which records it on the evaluation trial once. The grader's token then stops working, and its agent is stopped.",
      inputStructure: "detailed",
      successStatus: 204,
      successDescription: "The scorecard was recorded.",
    }),
  )
  .errors({
    ...errors.badRequest,
    ...refusals,
    CONFLICT: { message: "The trial already has a scorecard, or is no longer grading." },
  })
  .input(z.object({ params: GameIdParams, body: ScorecardSubmission }));

export const startPlayerAgent = oc
  .meta(
    paxRoute({ auth: serviceAuth, policy: { session: true } }),
    openapi({
      method: "POST",
      path: "/games/v1alpha1/{id}/player-agents",
      operationId: "games.v1alpha1.startPlayerAgent",
      summary: "Start an agent that plays a member's account.",
      description:
        "Starts the player agent with a player game token of its own, and hands it its persona as it starts. It connects and plays like anyone in a browser. Starting an agent for a member who already has one running returns that one.",
      inputStructure: "detailed",
      successStatus: 201,
      successDescription: "The player agent started.",
    }),
  )
  .errors({ ...errors.badRequest, ...refusals, ...errors.worldUnavailable })
  .input(z.object({ params: GameIdParams, body: StartPlayerAgentRequest }))
  .output(StartedAgent);

export const stopEval = oc
  .meta(
    paxRoute({ auth: serviceAuth, policy: { session: true } }),
    openapi({
      method: "POST",
      path: "/games/v1alpha1/{id}/eval-stop",
      operationId: "games.v1alpha1.stopEval",
      summary: "End play in an evaluation game.",
      description:
        "Stops its player agents and retires its Game Master. Nothing starts them again.",
      inputStructure: "detailed",
    }),
  )
  .errors(lobbyErrors)
  .input(z.object({ params: GameIdParams }))
  .output(LobbyCommandResponse);

export const searchAssets = oc
  .meta(
    paxRoute({ auth: agentGameTokenAuth, policy: { session: true } }),
    openapi({
      method: "GET",
      path: "/games/v1alpha1/{id}/assets/search",
      operationId: "games.v1alpha1.searchAssets",
      summary: "Search published assets for the game.",
      description:
        "Ranks published assets by how close their title, description, and tags are to `q`, searching as the game's owner: public collections, the private ones the owner keeps or collaborates on, and private official ones if the owner curates them. Sensitive assets are left out unless `includeSensitive` is `true`.",
      inputStructure: "detailed",
      successDescription: "The best matches first, each with its media, stats, and similarity.",
    }),
  )
  .errors({
    ...errors.badRequest,
    ...refusals,
    NOT_FOUND: { message: "The game, or a collection it names, isn't one it can search." },
    BAD_GATEWAY: { message: "Search is unavailable right now; try again shortly." },
  })
  .input(z.object({ params: GameIdParams, query: AssetSearchQuery }))
  .output(AssetSearchResponse);

const GameAssetParams = z.strictObject({ id: GameId, assetId: AssetId }).meta({
  id: "GameAssetParams",
  description: "Route parameters naming one game and one published asset.",
});

export const logAssetUsage = oc
  .meta(
    paxRoute({ auth: gameMasterGameTokenAuth, policy: { session: true } }),
    openapi({
      method: "POST",
      path: "/games/v1alpha1/{id}/assets/{assetId}/usage",
      operationId: "games.v1alpha1.logAssetUsage",
      summary: "Log that the game used an asset.",
      description:
        "Adds one to the asset's `useCount` and records which search found it. Sending the same `usageId` again counts nothing.",
      inputStructure: "detailed",
      successStatus: 204,
      successDescription: "The use is logged, now or by an earlier attempt with this `usageId`.",
    }),
  )
  .errors({
    ...errors.badRequest,
    ...refusals,
    NOT_FOUND: { message: "No published asset has that id." },
  })
  .input(z.object({ params: GameAssetParams, body: AssetUsageRequest }));
